/**
 * Subscriptions API (file: app/data/subscriptions/subscriptionsApi.ts)
 * This file contains functions to interact with the Subscriptions API endpoints.
 */

import { convertFromUTC } from 'app/utils/dates/conversions'
import { type AxiosInstance, AxiosError } from 'axios'
import { type JwtPayload } from 'jwt-decode'

// Extend the JwtPayload interface to include app_metadata
declare module 'jwt-decode' {
  interface JwtPayload {
    app_metadata?: {
      is_pending_deletion?: boolean
      has_access?: boolean
      is_payment_exempt?: boolean
      [key: string]: any
    }
  }
}

export interface SubscriptionData {
  id: string
  user_id?: string
  status: 'active' | 'trial' | 'cancelled'
  plan_id?: string
  starts_at?: string
  expires_at?: string
  renews_at?: string
  cancelled_at?: string
  trial_start_at?: string
  trial_end_at?: string
  created_at?: string
  updated_at?: string
  source?: 'apple' | 'playstore' | 'stripe'
  has_billing_issue: boolean
}

export interface SubscriptionInfo {
  isOnFreeTrial: boolean
  daysRemaining: number
  planType: string
  isProBono?: boolean
  source?: 'apple' | 'playstore' | 'stripe'
  autoRenewDate?: string
}

/**
 * Fetches current subscription details from the API
 *
 * @param axiosClient The Axios client instance
 * @param timezone The user's timezone
 * @returns {Promise<SubscriptionData>} A promise that resolves to subscription data
 */
export const fetchCurrentSubscription = async (
  axiosClient: AxiosInstance,
  timezone: string
): Promise<SubscriptionData> => {
  if (!timezone) {
    throw new Error('User timezone not available')
  }

  console.log('[fetchCurrentSubscription] Fetching subscription information', axiosClient, timezone)

  try {
    console.log('[fetchCurrentSubscription] About to make axios call')
    const response = await axiosClient.get('/subscriptions/current')
    console.log('[fetchCurrentSubscription] Axios call completed - Response:', response)
    // Check if response data exists
    if (!response.data) {
      throw new Error(`No subscription data received from server ${response.status}`)
    }

    // Check HTTP status code
    if (response.status !== 200) {
      throw new Error(`Unexpected HTTP status: ${response.status}`)
    }

    console.log('[fetchCurrentSubscription] Raw API response:', JSON.stringify(response.data, null, 2))

    const subscriptionData = response.data

    // Validate required fields
    if (!subscriptionData.id || !subscriptionData.status) {
      throw new Error('Invalid subscription data: missing required fields')
    }

    // Convert dates to user's timezone if they exist
    const convertDates = (data: SubscriptionData): SubscriptionData => {
      const converted = { ...data }

      // Convert each date field if it exists
      if (data.starts_at) {
        converted.starts_at = convertFromUTC(data.starts_at, timezone)
      }
      if (data.expires_at) {
        converted.expires_at = convertFromUTC(data.expires_at, timezone)
      }
      if (data.renews_at) {
        converted.renews_at = convertFromUTC(data.renews_at, timezone)
      }
      if (data.cancelled_at) {
        converted.cancelled_at = convertFromUTC(data.cancelled_at, timezone)
      }
      if (data.trial_start_at) {
        converted.trial_start_at = convertFromUTC(data.trial_start_at, timezone)
      }
      if (data.trial_end_at) {
        converted.trial_end_at = convertFromUTC(data.trial_end_at, timezone)
      }
      if (data.created_at) {
        converted.created_at = convertFromUTC(data.created_at, timezone)
      }
      if (data.updated_at) {
        converted.updated_at = convertFromUTC(data.updated_at, timezone)
      }

      return converted
    }

    const convertedData = convertDates(subscriptionData)
    console.log('fetchCurrentSubscription: Fetched subscription successfully', convertedData)

    return convertedData
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('Error fetching subscription:', error.response?.data)
    } else {
      console.error('Error fetching subscription:', error)
    }
    throw error
  }
}
